import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { boardViewsService } from '../api/board-views.service';
import { BOARD_KEYS } from './queryKeys';
import type { CreateBoardViewPayload, UpdateBoardViewPayload } from '../types';

export function useCreateBoardView(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: (data: CreateBoardViewPayload) => boardViewsService.create(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.views(activeOrganizationId, boardId),
      });
    },
  });
}

/**
 * Updates a board view's shared `config` — visible to every member with access to the board, not
 * just the caller (see the tasks feature's `parseListViewConfig` for the List view's shape).
 * Applies optimistically so column reorder/resize feels instant; rolls back on failure.
 */
export function useUpdateBoardView(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const queryKey = BOARD_KEYS.views(activeOrganizationId, boardId);

  return useMutation({
    mutationFn: ({ viewId, payload }: { viewId: string; payload: UpdateBoardViewPayload }) =>
      boardViewsService.update(boardId, viewId, payload),
    onMutate: async ({ viewId, payload }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<Awaited<ReturnType<typeof boardViewsService.getByBoard>>['data']>(
        queryKey,
        (views) => views?.map((v) => (v.id === viewId ? { ...v, config: payload.config } : v)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useDeleteBoardView(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: (viewId: string) => boardViewsService.delete(boardId, viewId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.views(activeOrganizationId, boardId),
      });
    },
  });
}
